import { z } from 'zod';

import { GENDERS, type Gender } from '../../users/gender';

/**
 * Who a question is asked of.
 *
 * This is the **data** form of gender-dependence (U15): an audience is a stored
 * attribute the form composer filters on, never an `if (gender === …)` branch.
 * "Ask women about their guardian" is one column value, set by staff, on a
 * question staff created — no code knows the question exists.
 *
 * Derived from `GENDERS` rather than written out, so adding a gender to the
 * platform cannot leave an audience value behind.
 */
export const QUESTION_AUDIENCES = ['all', ...GENDERS] as const;

export const QuestionAudienceSchema = z.enum(QUESTION_AUDIENCES);

export type QuestionAudience = (typeof QUESTION_AUDIENCES)[number];

/**
 * Whether a member of this gender is asked this question.
 *
 * Total and pure: the composer applies it to a whole flow in one pass, and the
 * clients apply the same predicate when previewing a form as another gender in
 * the admin builder (P10).
 */
export function isInAudience(audience: QuestionAudience, gender: Gender): boolean {
  return audience === 'all' || audience === gender;
}

const AUDIENCE_SET: ReadonlySet<string> = new Set<string>(QUESTION_AUDIENCES);

/** Narrows a plain string — what a filter query parameter passes through. */
export function isQuestionAudience(value: string): value is QuestionAudience {
  return AUDIENCE_SET.has(value);
}
